import { useState, useEffect, useRef } from "react";
import { mealTypeColors } from "../../constants/mealAccents";

function Skel({ w = "100%", h, r = "14px", style = {} }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r, flexShrink: 0,
      background: "linear-gradient(90deg,#E8DFD0 25%,#F4EDE2 50%,#E8DFD0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s ease infinite",
      ...style,
    }} />
  );
}

function HomeTab({
  isMobile,
  appTheme,
  today,
  plan,
  completedMeals,
  hoveredButton,
  setHoveredButton,
  getMealCardBackground,
  completeMeal,
  setActiveTab,
  openRecipeDetails,
  getDateKey,
  openQuickAddModal,
  nutritionTargets,
  currentStreak,
  profileCalendarMonthLabel,
  changeProfileCalendarMonth,
  profileCalendarCells,
  getProfileCalendarDateKey,
  todayDate,
  profileCalendarYear,
  profileCalendarMonth,
  hasCompletedMealOnDate,
  profileName,
  planningMode,
  selectedProgramme,
  currentProgrammeWeekIndex,
  currentProgrammeWeek,
  recipes,
  isLoading,
  removeSnack,
  openCookingMode,
  openEditSnackModal,
  onOpenSettings,
  profileImage,
}) {
  const mealTypes = ["breakfast", "lunch", "dinner"];
  const [snackOptionsSnack, setSnackOptionsSnack] = useState(null);
  const todayDateKey = getDateKey(new Date());

  // For programme mode, build todayPlan from the current programme week
  const todayDayName = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"][new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
  const todayPlan = (() => {
    if (planningMode === "programme" && currentProgrammeWeek?.days) {
      const progDay = currentProgrammeWeek.days[todayDayName] || {};
      const recipeByName = (name) => (recipes || []).find(r => r.name === name) || null;
      return {
        breakfast: progDay.breakfast ? recipeByName(progDay.breakfast) : null,
        lunch:     progDay.lunch     ? recipeByName(progDay.lunch)     : null,
        dinner:    progDay.dinner    ? recipeByName(progDay.dinner)    : null,
        snacks:    [],
      };
    }
    return plan[todayDateKey] || {};
  })();

  // Auto-select the most relevant meal based on time of day and cook status
  function getAutoMealIndex() {
    const hour = new Date().getHours();
    const timeIndex = hour < 11 ? 0 : hour < 16 ? 1 : 2;
    // From the time-appropriate meal onwards, find first uncompleted
    for (let i = timeIndex; i < mealTypes.length; i++) {
      if (!completedMeals[`${todayDateKey}-${mealTypes[i]}`]) return i;
    }
    // All meals at/after time slot are cooked — stay on dinner
    return 2;
  }

  const [activeMealIndex, setActiveMealIndex] = useState(getAutoMealIndex);

  // Re-evaluate whenever cook status or date changes
  useEffect(() => {
    setActiveMealIndex(getAutoMealIndex());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedMeals, todayDateKey]);

  const mealScrollRef = useRef(null);
  const mealCardRefs = useRef([]);

  // Centre the active card in the scroll container
  useEffect(() => {
    const container = mealScrollRef.current;
    const card = mealCardRefs.current[activeMealIndex];
    if (!container || !card) return;
    const containerCenter = container.offsetWidth / 2;
    const cardCenter = card.offsetLeft + card.offsetWidth / 2;
    container.scrollTo({ left: cardCenter - containerCenter, behavior: "smooth" });
  }, [activeMealIndex]);

  const cookedMeals = mealTypes.filter(
    (mt) => completedMeals[`${todayDateKey}-${mt}`]
  );
  const todaySnacks = todayPlan.snacks || [];

  // Calculate consumed macros from cooked meals + snacks
  const consumedMacros = cookedMeals.reduce((acc, mt) => {
    const meal = todayPlan[mt];
    if (!meal) return acc;
    return {
      calories: acc.calories + (meal.calories || 0),
      protein: acc.protein + (meal.macros?.protein || 0),
      carbs: acc.carbs + (meal.macros?.carbs || 0),
      fats: acc.fats + (meal.macros?.fats || 0)
    };
  }, { calories: 0, protein: 0, carbs: 0, fats: 0 });

  // Add snacks
  todaySnacks.forEach((snack) => {
    consumedMacros.calories += snack.calories || 0;
    consumedMacros.protein += snack.protein || 0;
    consumedMacros.carbs += snack.carbs || 0;
    consumedMacros.fats += snack.fats || 0;
  });

  const plannedMealCount = mealTypes.filter(mt => todayPlan[mt]).length;

  const totalMacroGrams = consumedMacros.protein + consumedMacros.carbs + consumedMacros.fats;
  const hasLoggedMacros = totalMacroGrams > 0;
  return (
    <>
{/* Welcome message */}
      <div style={{ marginBottom: "20px", paddingTop: "10px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "12px", marginBottom: "4px" }}>
          <div style={{
            fontFamily: appTheme.fontHeading,
            fontSize: isMobile ? "28px" : "34px",
            fontWeight: "600",
            color: appTheme.primaryText,
            lineHeight: 1.15
          }}>
            {(() => {
              const h = new Date().getHours();
              const greeting = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
              return `${greeting}, ${profileName?.split(" ")[0] || "there"}`;
            })()}
          </div>
          <button
            onClick={onOpenSettings}
            style={{
              width: isMobile ? "38px" : "42px",
              height: isMobile ? "38px" : "42px",
              borderRadius: "999px",
              overflow: "hidden",
              border: `2px solid ${appTheme.accentColor}22`,
              background: profileImage ? "transparent" : appTheme.accentColor,
              cursor: "pointer",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              alignSelf: "flex-start",
              transform: "translateY(-8px)",
            }}
          >
            {profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : (
              <span style={{ fontSize: "16px", fontWeight: "700", color: "#ffffff", lineHeight: 1, fontFamily: appTheme.fontBody }}>
                {profileName?.trim()?.[0]?.toUpperCase() || "?"}
              </span>
            )}
          </button>
        </div>
        <div style={{ fontSize: "14px", color: appTheme.secondaryText }}>
          Small changes. Big difference.
        </div>
      </div>

      {/* Plan mode banner */}
      {isLoading ? (
        <Skel h="88px" r="18px" style={{ marginBottom: "20px" }} />
      ) : (() => {
        if (planningMode === "programme" && selectedProgramme) {
          const totalWeeks = selectedProgramme.weeks?.length || 1;
          const currentWeek = (currentProgrammeWeekIndex || 0) + 1;
          const progress = Math.round((currentWeek / totalWeeks) * 100);
          const weeksRemaining = totalWeeks - currentWeek;
          return (
            <div style={{
              borderRadius: "18px",
              background: appTheme.accentColor,
              padding: "16px 18px",
              marginBottom: "20px",
            }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: "rgba(255,255,255,0.7)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>
                Active Programme
              </div>
              <div style={{ fontSize: "16px", fontWeight: "700", color: "#ffffff", marginBottom: "2px" }}>
                {selectedProgramme.name}
              </div>
              <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.8)", marginBottom: "12px" }}>
                Week {currentWeek} of {totalWeeks}{weeksRemaining > 0 ? ` · ${weeksRemaining} week${weeksRemaining === 1 ? "" : "s"} to go` : " · Final week!"}
              </div>
              <div style={{ height: "6px", borderRadius: "999px", background: "rgba(255,255,255,0.25)" }}>
                <div style={{
                  height: "100%",
                  borderRadius: "999px",
                  background: "#ffffff",
                  width: `${progress}%`,
                  transition: "width 0.4s ease",
                }} />
              </div>
            </div>
          );
        }

        // Manual mode — count meals planned this week
        const today = new Date();
        const dayOfWeek = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
        const mealTypes = ["breakfast", "lunch", "dinner"];
        const totalSlots = 7 * 3;
        const planned = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(monday);
          d.setDate(monday.getDate() + i);
          return getDateKey(d);
        }).reduce((acc, key) => acc + mealTypes.filter(mt => plan[key]?.[mt]).length, 0);
        return (
          <div style={{
            borderRadius: "18px",
            background: appTheme.accentColor,
            padding: "16px 18px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}>
            <div>
              <div style={{ fontSize: "11px", fontWeight: "700", color: "rgba(255,255,255,0.7)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>
                This week
              </div>
              <div style={{ fontSize: "22px", fontWeight: "800", color: "#ffffff", lineHeight: 1 }}>
                {planned} <span style={{ fontSize: "14px", fontWeight: "500", color: "rgba(255,255,255,0.8)" }}>of {totalSlots} meals planned</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "6px" }}>
                <svg width="13" height="16" viewBox="0 0 17 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.69188 0.173286C9.59942 0.0964175 9.48984 0.0429048 9.37237 0.017259C9.2549 -0.00838687 9.13298 -0.00541401 9.0169 0.0259268C8.90082 0.0572676 8.79398 0.116058 8.70538 0.197341C8.61678 0.278624 8.54902 0.380017 8.50781 0.492973L6.44531 6.15641L4.18031 3.96172C4.10425 3.88795 4.01341 3.83113 3.91379 3.79501C3.81417 3.75889 3.70802 3.74429 3.60235 3.75217C3.49667 3.76005 3.39387 3.79024 3.30071 3.84074C3.20755 3.89124 3.12615 3.96091 3.06188 4.04516C1.03125 6.70579 0 9.38235 0 11.9998C0 14.1879 0.869194 16.2863 2.41637 17.8335C3.96354 19.3807 6.06196 20.2498 8.25 20.2498C10.438 20.2498 12.5365 19.3807 14.0836 17.8335C15.6308 16.2863 16.5 14.1879 16.5 11.9998C16.5 6.42641 11.7384 1.87485 9.69188 0.173286ZM8.25 18.7498C6.4604 18.7479 4.74466 18.0361 3.47922 16.7706C2.21378 15.5052 1.50199 13.7894 1.5 11.9998C1.5 9.93735 2.25844 7.80079 3.75562 5.63985L6.22781 8.03797C6.3176 8.12517 6.42777 8.18851 6.5483 8.22222C6.66883 8.25593 6.79588 8.25895 6.91787 8.23099C7.03986 8.20303 7.15292 8.14499 7.24674 8.06215C7.34055 7.97931 7.41215 7.87431 7.455 7.75672L9.54281 2.03141C11.6447 3.94485 15 7.67891 15 11.9998C14.998 13.7894 14.2862 15.5052 13.0208 16.7706C11.7553 18.0361 10.0396 18.7479 8.25 18.7498Z" fill="rgba(255,255,255,0.7)"/>
                </svg>
                <span style={{ fontSize: "12px", fontWeight: "500", color: "rgba(255,255,255,0.75)" }}>
                  {currentStreak > 0 ? `${currentStreak} day streak` : "Start your streak today"}
                </span>
              </div>
            </div>
            <button
              onClick={() => setActiveTab("plan")}
              style={{
                padding: "9px 16px",
                borderRadius: "999px",
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.35)",
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Plan week
            </button>
          </div>
        );
      })()}

      {/* Today's meals */}
      <div style={{ marginBottom: "20px" }}>
        {/* Section header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
          <div style={{ fontSize: "17px", fontWeight: "600", fontFamily: appTheme.fontHeading, color: appTheme.primaryText }}>
            Today's meals
          </div>
        </div>

        {/* Horizontal scrollable cards */}
        <div ref={mealScrollRef} className="modal-scroll-hidden" style={{ display: "flex", gap: "10px", overflowX: "auto", paddingBottom: "4px" }}>
          {isLoading ? (
            ["breakfast", "lunch", "dinner", "snacks"].map((mealType) => (
              <Skel key={mealType} w="115px" h="190px" r="22px" style={{ flexShrink: 0 }} />
            ))
          ) : ["breakfast", "lunch", "dinner", "snacks"].map((mealType, index) => {
            const meal = index < 3 ? todayPlan[mealType] : null;
            const isActive = activeMealIndex === index;
            const isCooked = index < 3 && completedMeals[`${todayDateKey}-${mealType}`];
            const typeColor = mealTypeColors[mealType];

            if (mealType === "snacks") {
              return (
                <div
                  key="snacks"
                  ref={el => mealCardRefs.current[3] = el}
                  style={{
                    flexShrink: 0,
                    width: "115px",
                    height: "190px",
                    borderRadius: "22px",
                    overflow: "hidden",
                    background: appTheme.softBackground,
                    border: appTheme.cardBorder,
                    display: "flex",
                    flexDirection: "column",
                    padding: "12px 10px 10px",
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ fontSize: "12px", fontWeight: "700", color: appTheme.secondaryText, marginBottom: "6px" }}>
                    snacks
                  </div>

                  {todaySnacks.length === 0 ? (
                    <div
                      style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "14px", cursor: "pointer" }}
                      onClick={openQuickAddModal}
                    >
                      <span style={{ fontSize: "36px", fontWeight: "300", color: typeColor, lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: appTheme.accentColor, background: appTheme.accentLight, border: appTheme.accentBorder, padding: "5px 12px", borderRadius: "999px" }}>Quick Log</span>
                    </div>
                  ) : (
                    <>
                      <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "5px" }}>
                        {todaySnacks.map((snack) => (
                          <button
                            key={snack.id}
                            onClick={() => setSnackOptionsSnack(snack)}
                            style={{ padding: "6px 8px", borderRadius: "999px", background: appTheme.cardBackground, border: "1px solid rgba(160,144,106,0.4)", fontSize: "10px", fontWeight: "600", color: appTheme.primaryText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", textAlign: "left", flexShrink: 0, width: "100%", boxSizing: "border-box" }}
                          >
                            {snack.name}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={openQuickAddModal}
                        style={{ background: appTheme.accentLight, border: appTheme.accentBorder, cursor: "pointer", fontSize: "11px", fontWeight: "600", color: appTheme.accentColor, textAlign: "center", padding: "5px 12px", borderRadius: "999px", marginTop: "4px", flexShrink: 0, width: "100%", boxSizing: "border-box" }}
                      >
                        Quick Log
                      </button>
                    </>
                  )}
                </div>
              );
            }

            return (
              <div
                key={mealType}
                ref={el => mealCardRefs.current[index] = el}
                onClick={() => {
                  if (meal) openRecipeDetails(meal, true, planningMode === "programme"
                    ? { dateKey: `week${currentProgrammeWeekIndex}-${todayDayName}`, mealType, isProgramme: true }
                    : { dateKey: todayDateKey, mealType });
                }}
                style={{
                  flexShrink: 0,
                  width: "115px",
                  height: "190px",
                  borderRadius: "22px",
                  overflow: "hidden",
                  position: "relative",
                  cursor: "pointer",
                  transition: "all 0.22s ease",
                  opacity: !isActive && isCooked ? 0.55 : 1,
                  background: meal?.image
                    ? `url(${meal.image}) center/cover no-repeat`
                    : isActive ? appTheme.accentColor : appTheme.softBackground,
                  border: isActive && meal ? `2.5px solid ${appTheme.accentColor}` : appTheme.cardBorder,
                }}
              >
                {/* Dark gradient overlay when image present */}
                {meal?.image && (
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.1) 55%, transparent 100%)"
                  }} />
                )}

                {/* Chef's hat — top right */}
                {meal && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openCookingMode(meal, { dateKey: todayDateKey, mealType }); }}
                    style={{ position: "absolute", top: "8px", right: "8px", width: "28px", height: "28px", borderRadius: "8px", border: "none", background: appTheme.accentColor, color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 1 }}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"/>
                      <line x1="6" y1="17" x2="18" y2="17"/>
                    </svg>
                  </button>
                )}

                {/* Meal type label — top left */}
                <div style={{
                  position: "absolute", top: "12px", left: "12px",
                  fontSize: "12px", fontWeight: "700",
                  color: meal?.image ? "#ffffff" : isActive ? "rgba(255,255,255,0.9)" : appTheme.secondaryText,
                  textTransform: "capitalize",
                  textShadow: meal?.image ? "0 1px 4px rgba(0,0,0,0.4)" : "none",
                }}>
                  {mealType}
                </div>

                {/* Empty slot — centred coloured + */}
                {!meal && (
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ fontSize: "36px", fontWeight: "300", color: typeColor, lineHeight: 1 }}>+</span>
                  </div>
                )}

                {/* Bottom content */}
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  padding: "10px 10px 10px",
                }}>
                  {/* Meal name */}
                  <div style={{
                    fontSize: "12px", fontWeight: "700",
                    color: meal?.image ? "#ffffff" : isActive ? "#ffffff" : appTheme.primaryText,
                    lineHeight: 1.3, marginBottom: meal ? "6px" : "0",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}>
                    {meal?.name
                      ?? (index === 3
                        ? (todaySnacks.length > 0 ? `${todaySnacks.length} snack${todaySnacks.length > 1 ? "s" : ""}` : "")
                        : "")}
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Today's Nutrition */}
      {isLoading ? (
        <Skel h="140px" r="24px" style={{ marginBottom: "16px" }} />
      ) : (
      <div style={{
        marginBottom: "16px",
        padding: "14px 18px 16px",
        borderRadius: "24px",
        background: appTheme.cardBackground,
        border: appTheme.cardBorder,
        boxShadow: "0 4px 16px rgba(15,23,42,0.06)"
      }}>
        {/* Header row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
          <div style={{ fontSize: "17px", fontWeight: "600", color: appTheme.primaryText, fontFamily: appTheme.fontHeading }}>
            Today&apos;s Nutrition
          </div>
          <div style={{ fontSize: "13px", color: appTheme.secondaryText }}>
            {plannedMealCount === 0
              ? "No meals planned today"
              : `${cookedMeals.length}/${plannedMealCount} meals logged`}
          </div>
        </div>

        {/* Calorie total */}
        <div style={{ fontSize: "28px", fontWeight: "700", color: appTheme.primaryText, fontFamily: appTheme.fontHeading, lineHeight: 1, marginBottom: "12px" }}>
          {consumedMacros.calories > 0 ? consumedMacros.calories.toLocaleString() : "0"}
          <span style={{ fontSize: "15px", fontWeight: "500", color: appTheme.secondaryText, marginLeft: "6px" }}>kcal</span>
        </div>

        {/* Proportional macro bar */}
        <div style={{ height: "10px", borderRadius: "999px", background: appTheme.softBackground, overflow: "hidden", marginBottom: "8px" }}>
          {hasLoggedMacros ? (
            <div style={{ display: "flex", height: "100%", borderRadius: "999px", overflow: "hidden" }}>
              <div style={{ width: `${(consumedMacros.protein / totalMacroGrams) * 100}%`, background: "#C0392B", transition: "width 0.5s ease" }} />
              <div style={{ width: `${(consumedMacros.carbs   / totalMacroGrams) * 100}%`, background: "#f59e0b", transition: "width 0.5s ease" }} />
              <div style={{ width: `${(consumedMacros.fats    / totalMacroGrams) * 100}%`, background: "#5B8DB8", transition: "width 0.5s ease" }} />
            </div>
          ) : null}
        </div>

        {/* Macro legend / empty state */}
        {hasLoggedMacros ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", fontFamily: appTheme.fontBody }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#C0392B" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#C0392B", flexShrink: 0, display: "inline-block" }} />
              Protein {consumedMacros.protein}g
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#f59e0b" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#f59e0b", flexShrink: 0, display: "inline-block" }} />
              Carbs {consumedMacros.carbs}g
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#5B8DB8" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#5B8DB8", flexShrink: 0, display: "inline-block" }} />
              Fats {consumedMacros.fats}g
            </span>
          </div>
        ) : (
          <div style={{ fontSize: "12px", color: appTheme.mutedText, fontFamily: appTheme.fontBody }}>
            Log meals to see your breakdown
          </div>
        )}
      </div>
      )}

      {/* Snack options sheet */}
      {snackOptionsSnack && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 1700, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
            onClick={() => setSnackOptionsSnack(null)}
          />
          <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1701, background: appTheme.pageBackground, borderRadius: "24px 24px 0 0", padding: "14px 20px 32px", animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)" }}>
            <div style={{ width: "36px", height: "4px", borderRadius: "2px", background: "rgba(0,0,0,0.15)", margin: "0 auto 14px" }} />

            {/* Snack info row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "16px" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "15px", fontWeight: "600", color: appTheme.primaryText, marginBottom: "3px" }}>
                  {snackOptionsSnack.name}
                </div>
                <div style={{ fontSize: "12px", color: appTheme.secondaryText, display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {snackOptionsSnack.calories > 0 && (
                    <span>{snackOptionsSnack.calories} kcal</span>
                  )}
                  {snackOptionsSnack.protein > 0 && (
                    <><span>·</span><span>{snackOptionsSnack.protein}g protein</span></>
                  )}
                  {snackOptionsSnack.carbs > 0 && (
                    <><span>·</span><span>{snackOptionsSnack.carbs}g carbs</span></>
                  )}
                  {snackOptionsSnack.fats > 0 && (
                    <><span>·</span><span>{snackOptionsSnack.fats}g fats</span></>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setSnackOptionsSnack(null);
                  openEditSnackModal(snackOptionsSnack, todayDateKey);
                }}
                style={{ width: "36px", height: "36px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={appTheme.secondaryText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            </div>

            <button
              onClick={() => {
                removeSnack(todayDateKey, snackOptionsSnack.id);
                setSnackOptionsSnack(null);
              }}
              style={{ width: "100%", padding: "14px", borderRadius: "14px", border: "1px solid rgba(230,84,76,0.35)", background: "rgba(230,84,76,0.08)", color: "#E6544C", fontSize: "15px", fontWeight: "700", cursor: "pointer" }}
            >
              Remove snack
            </button>
          </div>
        </>
      )}
      <div style={{ height: isMobile ? "100px" : "40px" }} />
    </>
  );
}

export default HomeTab;